import {
  BookingStatus,
  CreditsReason,
  DateRange,
  GuestsSorting,
  GuestVipLevel,
  IDate,
  LeadSource,
  MembershipEventKind,
  MembershipPaymentStatus,
  MembershipStatus,
  PaginationInput,
  SortOrder,
  UUID,
  WalletTransactionKind,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { BadgeColors } from '@avantstay/backoffice-core'

export const colorByBookingStatus = {
  [BookingStatus.checkedOut]: BadgeColors.success,
  [BookingStatus.checkedIn]: BadgeColors.warning,
  [BookingStatus.booked]: BadgeColors.accent,
  [BookingStatus.cancelled]: BadgeColors.failure,
} as Record<BookingStatus, (typeof BadgeColors)[keyof typeof BadgeColors]>

export interface GuestBookingCustomFields {
  section: {
    id: string
    name: string
  }
  fields: {
    id: string
    name: string
    value?: string
  }[]
}

export interface Refunds {
  total?: Cents
  reasons?: string[]
}

export interface GuestBookingFolio {
  summary: {
    value: number
    total: number
    isPaid: boolean
    balance: number
    paid: {
      total: number
      credits: number
    }
  }
}

export interface GuestBookingHome {
  name: string
  title: string
  region?: {
    name: string
  }
}

export interface GuestBooking {
  id: string
  hash: string
  source: LeadSource
  status: BookingStatus
  period: DateRange
  home: GuestBookingHome
  folio: GuestBookingFolio
  customFields: GuestBookingCustomFields[]
  hasDamage?: boolean
  damageHasBeenPaid?: boolean
  refunds?: Refunds
}

export interface BookingResults {
  total?: number
  pages?: number
  results: GuestBooking[]
}

export interface GuestBookingsResponse {
  ongoing: GuestBooking[]
  upcoming: BookingResults
  previous: BookingResults
  canceled: BookingResults
}

export interface Operator {
  id: string
  name: string
}

export interface GuestProjectionCreditHistoryFragment {
  transaction: {
    amount: number
    id: string
    personId: string
    kind: WalletTransactionKind
    metadata?: string
    parentTransactionId?: string
  }
  expiration?: IDate
  reason?: CreditsReason
  received?: IDate
  inviterOrSubscriber?: string
  operators: {
    requestOperator?: Operator
    editingOperator?: Operator
  }
  balance?: number
}

export interface UpdateGuestVariables {
  id: string
  tagsIds?: number[]
  note?: string
}

export interface UpdateGuestNameVariables {
  id: string
  fullName: string
}
export interface RemoveGuestVariables {
  bookingId: string
  personId: string
}

export interface GuestCredit {
  personId: string
  cents: number
  expiration: string
  reason: CreditsReason
  relatedBookingId?: string
}

export interface GuestCreditWithdraw {
  personId: string
  walletTransactionId: string
  amount?: number
  reason: CreditsReason
  relatedBookingId?: string
}

export interface GuestCreditEdit {
  personId: string
  walletTransactionId: string
  amount?: number
  expiration: IDate
}

export interface AdditionalEmailsProps {
  originalEmail?: string
  value: string
}

export interface GeneralInfoFormValues {
  name: string
  email: string
  additionalEmails: AdditionalEmailsProps[]
  phone: string
  address: string
  note: string
  tags: { label: string; value: string | number }[]
}

export interface MutateGuestAdditionalEmailsVariables {
  id: string
  email: string
}

export interface UpdateGuestAdditionalEmailsVariables {
  id: string
  additionalEmails?: AdditionalEmailsProps[]
  originalEmails: AdditionalEmailsProps[]
}

export interface EmailsToUpdateProps {
  emailsToAdd: string[]
  emailsToRemove: string[]
}

export interface GetGuestsListVariables {
  pagination: PaginationInput
  terms?: string
  guestTags?: number[]
  sortOrder?: SortOrder
  sorting?: GuestsSorting
}

interface GuestMembershipCancellation {
  reason?: string
  cancelledAt?: IDate
  cancelledBy?: string
}

export interface GuestMembershipChange {
  eventDate: IDate
  eventKind: MembershipEventKind
}

export interface GuestMembershipPaymentRefund {
  id: string
  amount: number
}

export interface GuestMembershipPaymentSource {
  kind?: string
  last4?: string
  brand?: string
}

export interface GuestMembershipPaymentStatusChange {
  status: MembershipPaymentStatus
  time: IDate
}

export interface GuestMembershipPayment {
  paymentId: string
  userMembershipId: string
  title: string
  amount: number
  status: MembershipPaymentStatus
  source?: GuestMembershipPaymentSource
  refunds: GuestMembershipPaymentRefund[]
  statusTrack?: GuestMembershipPaymentStatusChange[]
}

export interface GuestMembershipInfo {
  id: string
  status: MembershipStatus
  startsAt: IDate
  isActive: boolean
  endsAt?: IDate
  renewalDate?: IDate
  planId?: string
  amountCents?: number
  cancellation?: GuestMembershipCancellation
  changesHistory: GuestMembershipChange[]
  paymentHistory?: GuestMembershipPayment[]
}

export enum GuestInfoTabs {
  GUEST_INFO,
  TRAVEL_CREDITS,
  BOOKINGS,
  MEMBERSHIP,
}

export interface GuestTags {
  tag: {
    name: string
    id: number
    vipLevel?: GuestVipLevel
  }
}

export interface AddBookingGuestVariables {
  bookingId: UUID
  guestId: UUID
  tagsIds?: number[]
  note?: string
}

export interface CreateBookingGuestVariables {
  bookingId: UUID
  fullName: string
  email: string
  phone?: string
  tagsIds?: number[]
  note?: string
}
